import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { tokens, adminJobs, adminSeekAds } from '../utils/api';
import toast from 'react-hot-toast';

export default function ModeratorPanel() {
  const { t, user, fetchBalance } = useApp();
  const navigate     = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('requests');
  const [acting,   setActing]   = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectingId, setRejectingId] = useState(null);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobPage, setJobPage] = useState(1);
  const [jobTotalPages, setJobTotalPages] = useState(1);
  const [jobFilterUser, setJobFilterUser] = useState('');

  // Seek Ads state
  const [seekAdsList, setSeekAdsList] = useState([]);
  const [seekPage, setSeekPage] = useState(1);
  const [seekTotalPages, setSeekTotalPages] = useState(1);
  const [seekFilterUser, setSeekFilterUser] = useState('');

  useEffect(() => {
    if (!user || !['MODERATOR','SUPERADMIN'].includes(user.role)) { navigate('/'); return; }
    tokens.pending()
      .then(r => setRequests(r.requests ?? []))
      .catch(() => toast.error(t('error_occurred')))
      .finally(() => setLoading(false));
  }, [user, navigate, t]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = { page: jobPage, perPage: 10 };
      if (jobFilterUser) params.poster_id = jobFilterUser;
      const res = await adminJobs.list(params);
      setJobs(res.jobs || []);
      setJobTotalPages(res.totalPages || 1);
    } catch { toast.error('Failed to load jobs'); }
  }, [jobPage, jobFilterUser]);

  const fetchSeekAds = useCallback(async () => {
    try {
      const params = { page: seekPage, perPage: 10 };
      if (seekFilterUser) params.seeker_id = seekFilterUser;
      const res = await adminSeekAds.list(params);
      setSeekAdsList(res.seekAds || []);
      setSeekTotalPages(res.totalPages || 1);
    } catch { toast.error('Failed to load seek ads'); }
  }, [seekPage, seekFilterUser]);

  useEffect(() => {
    if (tab === 'jobs') fetchJobs();
    if (tab === 'seek-ads') fetchSeekAds();
  }, [tab, fetchJobs, fetchSeekAds]);

  const handleApprove = async id => {
    setActing(id);
    try {
      await tokens.approve(id);
      fetchBalance();
      toast.success('Approved!');
      setRequests(rs => rs.filter(r => r.id !== id));
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setActing(null); }
  };

  const handleReject = async id => {
    setActing(id);
    try {
      await tokens.reject(id, { rejectNote });
      toast.success('Rejected.');
      setRequests(rs => rs.filter(r => r.id !== id));
      setRejectingId(null); setRejectNote('');
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setActing(null); }
  };

  const handleCloseJob = async id => {
    if(!window.confirm('Are you sure you want to close this job?')) return;
    try {
      await adminJobs.close(id);
      toast.success('Job closed');
      fetchJobs();
    } catch (err) { toast.error(err.error || 'Error closing job'); }
  };

  const handleDeleteJob = async id => {
    if(!window.confirm('Are you sure you want to delete this job permanently?')) return;
    try {
      await adminJobs.delete(id);
      toast.success('Job deleted');
      fetchJobs();
    } catch (err) { toast.error(err.error || 'Error deleting job'); }
  };

  const handleCloseSeekAd = async id => {
    if(!window.confirm('Are you sure you want to close this seek ad?')) return;
    try {
      await adminSeekAds.close(id);
      toast.success('Seek ad closed');
      fetchSeekAds();
    } catch (err) { toast.error(err.error || 'Error closing seek ad'); }
  };

  const handleDeleteSeekAd = async id => {
    if(!window.confirm('Are you sure you want to delete this seek ad permanently?')) return;
    try {
      await adminSeekAds.delete(id);
      toast.success('Seek ad deleted');
      fetchSeekAds();
    } catch (err) { toast.error(err.error || 'Error deleting seek ad'); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  const pending = requests.filter(r => r.status === 'PENDING');

  return (
    <div className="container" style={{ paddingTop:'36px', paddingBottom:'60px' }}>
      <div style={{ marginBottom:'28px' }}>
        <h1 style={{ fontSize:'24px', fontWeight:700 }}>{t('moderator_panel')}</h1>
        <p style={{ color:'var(--text3)', fontSize:'14px', marginTop:'4px' }}>
          {user?.moderatorField ?? 'General'} Moderator · {user?.fullName}
        </p>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ marginBottom:'24px' }}>
        <div className="stat-card brand"><div className="val">{pending.length}</div><div className="lbl">Pending</div></div>
        <div className="stat-card"><div className="val">{requests.filter(r=>r.status==='APPROVED').length}</div><div className="lbl">Approved today</div></div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'16px', borderBottom:'1px solid var(--border)' }}>
        {[
          ['requests', `${t('token_requests')}${pending.length>0?` (${pending.length})`:''}`],
          ['jobs', 'Job Post'],
          ['seek-ads', 'Job Seek Post']
        ].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding:'10px 16px', fontSize:'14px', fontWeight:500, border:'none', background:'transparent', cursor:'pointer', color: tab===k?'var(--brand)':'var(--text3)', borderBottom: tab===k?'2px solid var(--brand)':'2px solid transparent', marginBottom:'-1px', transition:'all var(--transition)' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Token requests */}
      {tab === 'requests' && (
        <div className="stagger">
          {pending.length === 0 ? (
            <div className="empty-state"><div style={{fontSize:'40px',marginBottom:'10px'}}>🎉</div><p>No pending token requests!</p></div>
          ) : pending.map(req => (
            <div key={req.id} className="card" style={{ marginBottom:'10px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
                <div style={{ flex:1, minWidth:'200px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' }}>
                    <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--brand-light)', color:'var(--brand-dark)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, flexShrink:0 }}>
                      {req.user_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:600, fontSize:'14px' }}>{req.user_name}</div>
                      <div style={{ fontSize:'12px', color:'var(--text3)' }}>{req.user_email} · {req.user_role}</div>
                    </div>
                  </div>
                  <div style={{ background:'var(--surface2)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:'13px' }}>
                    <div>Requesting <strong>{req.amount} tokens</strong></div>
                    <div style={{ color:'var(--text2)', marginTop:'4px' }}>Reason: {req.reason}</div>
                    <div style={{ color:'var(--text3)', fontSize:'11px', marginTop:'4px' }}>
                      {new Date(req.created_at).toLocaleString('en-BD', { dateStyle:'medium', timeStyle:'short' })}
                    </div>
                  </div>
                  {/* Reject note field */}
                  {rejectingId === req.id && (
                    <div style={{ marginTop:'10px' }}>
                      <input className="form-input" placeholder="Rejection reason (optional)" value={rejectNote}
                        onChange={e => setRejectNote(e.target.value)} style={{ fontSize:'13px' }} />
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'6px', alignItems:'flex-end', flexShrink:0 }}>
                  <span className="chip chip-amber">PENDING</span>
                  <button className="btn btn-success btn-sm" disabled={!!acting} onClick={() => handleApprove(req.id)}>
                    {acting === req.id ? <span className="spinner" style={{width:12,height:12}}/> : t('approve')}
                  </button>
                  {rejectingId === req.id ? (
                    <div style={{ display:'flex', gap:'6px' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setRejectingId(null)}>Cancel</button>
                      <button className="btn btn-danger btn-sm" disabled={!!acting} onClick={() => handleReject(req.id)}>Confirm</button>
                    </div>
                  ) : (
                    <button className="btn btn-danger btn-sm" onClick={() => setRejectingId(req.id)}>{t('reject')}</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Jobs */}
      {tab === 'jobs' && (
        <div className="stagger">
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <input 
              type="text" 
              className="form-input" 
              placeholder="Filter by Post User ID" 
              value={jobFilterUser}
              onChange={e => setJobFilterUser(e.target.value)}
              style={{ maxWidth: '300px' }}
            />
            <button className="btn btn-primary" onClick={() => { setJobPage(1); fetchJobs(); }}>Search</button>
          </div>
          
          {jobs.length === 0 ? (
            <div className="empty-state">No jobs found</div>
          ) : jobs.map(job => (
            <div key={job.id} className="card" style={{ marginBottom:'10px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>#{job.id} - {job.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>
                    Poster: {job.posterName} (ID: {job.posterId}) · {job.type} · ৳{Number(job.budget).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                  <span className={`chip ${job.status === 'OPEN' ? 'chip-green' : job.status === 'CLOSED' ? 'chip-gray' : 'chip-blue'}`}>
                    {job.status}
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {job.status !== 'CLOSED' && (
                      <button className="btn btn-outline btn-sm" onClick={() => handleCloseJob(job.id)}>Close</button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteJob(job.id)}>Delete</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Pagination */}
          {jobTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
              <button className="btn btn-outline" disabled={jobPage <= 1} onClick={() => setJobPage(p => p - 1)}>Prev</button>
              <span style={{ display: 'flex', alignItems: 'center' }}>{jobPage} / {jobTotalPages}</span>
              <button className="btn btn-outline" disabled={jobPage >= jobTotalPages} onClick={() => setJobPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* Seek Ads */}
      {tab === 'seek-ads' && (
        <div className="stagger">
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <input 
              type="text" 
              className="form-input" 
              placeholder="Filter by Seek User ID" 
              value={seekFilterUser}
              onChange={e => setSeekFilterUser(e.target.value)}
              style={{ maxWidth: '300px' }}
            />
            <button className="btn btn-primary" onClick={() => { setSeekPage(1); fetchSeekAds(); }}>Search</button>
          </div>
          
          {seekAdsList.length === 0 ? (
            <div className="empty-state">No seek ads found</div>
          ) : seekAdsList.map(ad => (
            <div key={ad.id} className="card" style={{ marginBottom:'10px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>#{ad.id} - {ad.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>
                    Seeker: {ad.seekerName} (ID: {ad.seekerId}) · ৳{Number(ad.rate).toLocaleString()}/{ad.rateType}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                  <span className={`chip ${ad.status === 'ACTIVE' ? 'chip-green' : ad.status === 'CLOSED' ? 'chip-gray' : 'chip-blue'}`}>
                    {ad.status}
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {ad.status !== 'CLOSED' && (
                      <button className="btn btn-outline btn-sm" onClick={() => handleCloseSeekAd(ad.id)}>Close</button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSeekAd(ad.id)}>Delete</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Pagination */}
          {seekTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
              <button className="btn btn-outline" disabled={seekPage <= 1} onClick={() => setSeekPage(p => p - 1)}>Prev</button>
              <span style={{ display: 'flex', alignItems: 'center' }}>{seekPage} / {seekTotalPages}</span>
              <button className="btn btn-outline" disabled={seekPage >= seekTotalPages} onClick={() => setSeekPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
